import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useI18n, categoryLabel } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const COLOR_PRESETS = ["#2C4C3B", "#3A6B53", "#C98A3A", "#A44C40", "#3F5C7A", "#6B8F71", "#7A5C44", "#8A5A9E", "#5E6F58"];

export default function Categories() {
  const { t, lang } = useI18n();
  const [cats, setCats] = useState([]);
  const [modal, setModal] = useState(null); // null | "new" | category object

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/categories");
      setCats(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (cat) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try {
      await api.delete(`/categories/${cat.id}`);
      toast.success(t("deleted"));
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.15em] text-[var(--text-muted)] mb-2">
            {t("categories")}
          </p>
          <h1 className="font-display text-4xl font-bold tracking-tight leading-none">
            {lang === "es" ? "Tus categorías" : "Your categories"}
          </h1>
        </div>
        <Button onClick={() => setModal("new")} className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white" data-testid="add-category-btn">
          <Plus size={14} className="mr-1.5" /> {t("addCategory")}
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {cats.map((c) => (
          <div key={c.id} className="bg-[var(--surface)] border border-[var(--border-color)] rounded-lg p-4 flex items-center justify-between card-lift" data-testid={`category-${c.name}`}>
            <div className="flex items-center gap-3 min-w-0">
              <span className="w-8 h-8 rounded-md grid place-items-center shrink-0" style={{ background: (c.color || "#2C4C3B") + "22" }}>
                <span className="w-3 h-3 rounded-full" style={{ background: c.color }} />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{categoryLabel(c, lang)}</p>
                <p className="text-xs text-[var(--text-muted)] truncate font-mono">{c.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setModal(c)} data-testid={`edit-cat-${c.name}`}>
                <Pencil size={14} />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-[var(--status-negative)]" onClick={() => remove(c)} data-testid={`delete-cat-${c.name}`}>
                <Trash2 size={14} />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <CategoryModal cat={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
    </div>
  );
}

function CategoryModal({ cat, onClose, onSaved }) {
  const { t, lang } = useI18n();
  const isEdit = cat && cat !== "new";
  const [form, setForm] = useState({ name: "", label_es: "", label_en: "", color: "#2C4C3B", kind: "other" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isEdit) {
      setForm({
        name: cat.name,
        label_es: cat.label_es || "",
        label_en: cat.label_en || "",
        color: cat.color || "#2C4C3B",
        kind: cat.kind || "other",
      });
    } else {
      setForm({ name: "", label_es: "", label_en: "", color: "#2C4C3B", kind: "other" });
    }
  }, [cat, isEdit]);

  if (!cat) return null;

  const save = async () => {
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/categories/${cat.id}`, form);
      } else {
        await api.post("/categories", form);
      }
      toast.success(t("saved"));
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!cat} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="category-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{isEdit ? t("edit") : t("addCategory")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-[var(--text-secondary)]">{lang === "es" ? "Clave" : "Key"}</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "") })}
              disabled={isEdit}
              data-testid="cat-name-input"
              placeholder="e.g. groceries"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-[var(--text-secondary)]">{t("labelEs")}</Label>
            <Input value={form.label_es} onChange={(e) => setForm({ ...form, label_es: e.target.value })} data-testid="cat-label-es" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-[var(--text-secondary)]">{t("labelEn")}</Label>
            <Input value={form.label_en} onChange={(e) => setForm({ ...form, label_en: e.target.value })} data-testid="cat-label-en" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-[var(--text-secondary)]">{t("color")}</Label>
            <div className="flex flex-wrap gap-2">
              {COLOR_PRESETS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setForm({ ...form, color: c })}
                  className={`w-8 h-8 rounded-md border-2 ${form.color === c ? "border-[var(--text-primary)]" : "border-transparent"}`}
                  style={{ background: c }}
                  data-testid={`color-${c}`}
                />
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("cancel")}</Button>
          <Button onClick={save} disabled={saving || !form.name} className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white" data-testid="cat-save-btn">
            {saving ? "..." : t("save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
